import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';

type TruncatedCellProps = {
  tooltipText: string;
  text?: string;
  value?: React.ReactNode;
  prefix?: React.ReactNode;
  textSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  textVariant?: TypographyProps['variant'];
};

const TruncatedCell: React.FC<TruncatedCellProps> = ({ tooltipText, text, value, prefix, textSx, containerSx, textVariant = 'body2' }) => {
  const textRef = React.useRef<HTMLSpanElement | null>(null);
  const [isOverflowed, setIsOverflowed] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    if (!textRef.current) return;
    const { scrollWidth, clientWidth } = textRef.current;
    setIsOverflowed(scrollWidth > clientWidth);
  }, []);

  React.useLayoutEffect(() => {
    checkOverflow();
    const element = textRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(element);
    return () => observer.disconnect();
  }, [checkOverflow, tooltipText]);

  const showTooltip = Boolean(tooltipText) && isOverflowed;

  return (
    <Tooltip title={showTooltip ? tooltipText : ''} disableHoverListener={!showTooltip} placement='top' arrow>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
          ...(containerSx ?? {}),
        }}
      >
        {prefix ? (
          <Box component='span' sx={{ flexShrink: 0, mr: 0.5 }}>
            {prefix}
          </Box>
        ) : null}
        {text !== undefined ? (
          <Typography
            component='span'
            variant={textVariant}
            ref={textRef}
            onMouseEnter={checkOverflow}
            sx={{
              display: 'block',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              ...(textSx ?? {}),
            }}
          >
            {text}
          </Typography>
        ) : (
          <Box
            component='span'
            ref={textRef}
            onMouseEnter={checkOverflow}
            sx={{
              display: 'block',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              ...(textSx ?? {}),
            }}
          >
            {value}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default TruncatedCell;
